import React, { memo, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { useFormatDate } from '~/i18n/hooks/useFormatDate/useFormatDate';

import { User } from '../../types/User';
import { getUserInitials } from '../../utils/getUserInitials';
import { getUserName } from '../../utils/getUserName';
import { Image } from '../Image/Image';
import { Layout } from '../Layout/Layout';
import { Text } from '../Text/Text';
import { Tooltip } from '../Tooltip/Tooltip';
import { UserAvatarOptions } from './types';

interface Props {
  user?: User;
  placeholder?: string;
  options?: UserAvatarOptions;
}

export const UserAvatar = memo(
  ({ options = {}, user, placeholder: initialPlaceholder }: Props) => {
    const intl = useIntl();
    const formatDate = useFormatDate();

    const placeholder = initialPlaceholder
      ? initialPlaceholder
      : intl.formatMessage({
          id: 'UserAvatar.placeholder',
          defaultMessage: 'Unknown User',
          description:
            'Placeholder text for user avatar when no user data is available',
        });

    const { date, disableTooltip, size } = options;
    const isLarge = size === 'l';
    const avatarSize = isLarge ? 40 : 24;
    const textVariant = isLarge ? 'body-medium' : 'tiny';

    const tooltip = user
      ? `${getUserName({ user, intl })}${
          date ? '\n' + formatDate(date, 'short_datetime') : ''
        }`
      : placeholder;

    const isInactiveUser = !user || user.isDeleted;

    const avatar = useMemo(() => {
      if (user) {
        return Boolean(user.avatar) && !user.isDeleted ? (
          <Image
            width={avatarSize - 2}
            height={avatarSize - 2}
            borderRadius="50%"
            src={user.avatar}
            alt="User Avatar Picture"
          />
        ) : (
          <Text variant={textVariant}>{getUserInitials(user)}</Text>
        );
      } else {
        return <Text variant={textVariant}>?</Text>;
      }
    }, [user]);

    return (
      <Tooltip tooltip={disableTooltip ? null : tooltip}>
        <Layout
          align="center"
          bg={isInactiveUser ? 'background.quiet' : 'background.secondary'}
          styles={{ opacity: isInactiveUser ? 0.7 : 1 }}
          border="border"
          borderRadius="round"
          flex="none"
          h={avatarSize}
          maxW={avatarSize}
          justify="center"
          w={avatarSize}
        >
          {avatar}
        </Layout>
      </Tooltip>
    );
  },
);
